import React from "react";
import { useReveal } from "../../hooks/useReveal";
import { Ornament, StarGlyph, CuneiformMotif } from "../Ornaments";

export const Energetics = () => {
  const head = useReveal();
  const body = useReveal();
  const quote = useReveal();

  return (
    <section
      id="energetics"
      data-testid="energetics-section"
      style={{
        position: "relative",
        padding: "7rem 1.5rem",
        background: "linear-gradient(180deg, #0a0806 0%, #14100c 50%, #0a0806 100%)",
        overflow: "hidden",
      }}
    >
      <div className="violet-glow" style={{ width: 700, height: 700, top: -150, left: "55%", opacity: 0.6 }} />
      <div className="lapis-glow" style={{ width: 600, height: 600, bottom: -200, left: -150, opacity: 0.7 }} />

      <div className="section-inner" style={{ maxWidth: 1100 }}>
        <div ref={head.ref} className={`reveal ${head.inView ? "in-view" : ""}`} style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 3.5rem" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ornament centered />
          </div>
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            Energetics — The Why
          </p>
          <h2 className="section-title" data-testid="energetics-title">
            The Body Is a Grid <br />
            That Must Be Charged
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "3rem",
            alignItems: "start",
          }}
          className="energetics-grid"
        >
          {/* Quote card — verbatim Ben */}
          <figure
            ref={quote.ref}
            className={`reveal ${quote.inView ? "in-view" : ""}`}
            data-testid="energetics-quote"
            style={{
              position: "relative",
              margin: 0,
              background: "linear-gradient(180deg, rgba(20,16,12,0.85) 0%, rgba(10,8,6,0.92) 100%)",
              border: "1px solid rgba(201, 168, 76, 0.25)",
              borderLeft: "3px solid var(--gold)",
              padding: "2.5rem 2rem",
            }}
          >
            <div style={{ position: "absolute", top: 14, right: 18, opacity: 0.5 }} aria-hidden="true">
              <StarGlyph size={14} />
            </div>
            <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.65rem", letterSpacing: "0.4em", marginBottom: "1.25rem" }}>
              Benjamin D&rsquo;Aguilar — In His Own Words
            </p>
            <blockquote
              style={{
                margin: 0,
                fontFamily: "var(--font-body)",
                fontStyle: "italic",
                color: "var(--text)",
                fontSize: "clamp(1.05rem, 1.4vw, 1.18rem)",
                lineHeight: 1.75,
              }}
            >
              <p style={{ marginBottom: "1rem" }}>
                <span style={{ color: "var(--gold-bright)" }}>&ldquo;</span>The reason why energy and energetics are a
                foundation of this practice — same way with the weather. A lightning storm comes through,
                charges the land, charges the grid. Then the land can afford the physical and material changes.
              </p>
              <p style={{ marginBottom: "1rem" }}>
                The human body is the same way. You&rsquo;re paying yourself energetically, doing the practice,
                fueling yourself again with energy and electricity — so then you can afford the physical
                and material changes.
              </p>
              <p style={{ marginBottom: 0 }}>
                A television has all the hardware and software to operate properly — but without energy
                and electricity, even with all those capabilities, it doesn&rsquo;t work. It doesn&rsquo;t turn on
                unless you have the energy to charge it. <span style={{ color: "var(--gold-bright)" }}>Energetics. The why.</span>
                <span style={{ color: "var(--gold-bright)" }}>&rdquo;</span>
              </p>
            </blockquote>
            <div style={{ marginTop: "1.5rem", opacity: 0.55 }}>
              <CuneiformMotif width={140} />
            </div>
          </figure>

          {/* Practice in motion — muted field recording */}
          <figure
            className="charge-transmission"
            data-testid="practice-motion"
            style={{ margin: "0 auto", maxWidth: 300 }}
          >
            <p
              className="font-heading"
              style={{
                color: "var(--gold)",
                fontSize: "0.6rem",
                letterSpacing: "0.42em",
                marginBottom: "0.85rem",
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              ✦ &nbsp; The Practice &nbsp; ✦
            </p>
            <div className="charge-transmission-frame">
              <video
                src="/media/practice-motion.mp4"
                poster="/media/practice-motion-poster.jpg"
                muted
                autoPlay
                loop
                playsInline
                preload="metadata"
                data-testid="practice-motion-video"
              />
            </div>
            <figcaption
              className="font-heading"
              style={{
                color: "var(--text-dim)",
                fontSize: "0.58rem",
                letterSpacing: "0.35em",
                marginTop: "0.85rem",
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              Field Recording &nbsp;·&nbsp; The Grid in Motion
            </figcaption>
          </figure>

          {/* Right column — gloss + transmission video */}
          <div ref={body.ref} className={`reveal ${body.inView ? "in-view" : ""}`}>
            {/* Transmission video — vertical, framed, autoplay-loop-muted */}
            <figure
              className="charge-transmission"
              data-testid="charge-transmission"
              style={{ margin: "0 0 2rem", float: "right", marginLeft: "1.5rem" }}
            >
              <p
                className="font-heading"
                style={{
                  color: "var(--gold)",
                  fontSize: "0.6rem",
                  letterSpacing: "0.42em",
                  marginBottom: "0.85rem",
                  textAlign: "center",
                  textTransform: "uppercase",
                }}
              >
                ✦ &nbsp; Transmission &nbsp; ✦
              </p>
              <div className="charge-transmission-frame">
                <video
                  src="/media/charge-transmission.mp4"
                  poster="/media/charge-transmission-poster.jpg"
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                  data-testid="charge-transmission-video"
                />
              </div>
              <figcaption
                className="font-heading"
                style={{
                  color: "var(--text-dim)",
                  fontSize: "0.58rem",
                  letterSpacing: "0.35em",
                  marginTop: "0.85rem",
                  textAlign: "center",
                  textTransform: "uppercase",
                }}
              >
                Motion Data &nbsp;·&nbsp; The Body Charged
              </figcaption>
            </figure>

            <p className="lead" data-testid="energetics-gloss">
              Before any technique can do its work, the system has to be <em style={{ color: "var(--gold-bright)" }}>charged</em>.
              Lightning charges the land. The land then affords the changes. The body runs on
              the same principle.
            </p>
            <p className="lead lead-muted" style={{ marginTop: "1.5rem" }}>
              This is why coherence is not a soft concept and self-cultivation is not an
              affirmation. Both depend on a body that has been actively, intentionally,
              <em> energetically</em> charged. Movement is the practice of paying yourself
              back into your own grid.
            </p>
            <div
              style={{
                marginTop: "2.25rem",
                paddingTop: "1.5rem",
                borderTop: "1px solid rgba(201,168,76,0.18)",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1.5rem",
              }}
            >
              <div data-testid="energetics-card-charge">
                <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.68rem", letterSpacing: "0.32em", marginBottom: "0.5rem" }}>
                  Charge
                </p>
                <p style={{ color: "var(--text-muted)", fontSize: "0.92rem", lineHeight: 1.65 }}>
                  Energy first. The grid cannot reorganize without it.
                </p>
              </div>
              <div data-testid="energetics-card-afford">
                <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.68rem", letterSpacing: "0.32em", marginBottom: "0.5rem" }}>
                  Afford the Change
                </p>
                <p style={{ color: "var(--text-muted)", fontSize: "0.92rem", lineHeight: 1.65 }}>
                  A charged system can carry physical, material, and energetic change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
